import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { IcPageHeader, IcButton } from "@ukic/react";

import { api, type EntitiesResponse } from "../api";
import { timeAgo } from "../format";
import { DataTable, type Column } from "../components/DataTable";
import { Loading } from "../components/Loading";
import { ErrorState } from "../components/ErrorState";

const dash = <span className="hl-muted">—</span>;

function loc(l: { lat: number; lon: number; radius_km: number | null } | null) {
  if (!l) return dash;
  const r = l.radius_km ? ` ±${l.radius_km}km` : "";
  return (
    <Link to="/map">
      <code>{l.lat.toFixed(3)},{l.lon.toFixed(3)}</code>{r}
    </Link>
  );
}

export default function Entities() {
  const [data, setData] = useState<EntitiesResponse | null>(null);
  const [error, setError] = useState<unknown>(null);

  const load = useCallback(() => {
    setError(null);
    api.listEntities(200).then(setData).catch(setError);
  }, []);

  useEffect(() => {
    load();
    const t = window.setInterval(load, 20_000);
    return () => window.clearInterval(t);
  }, [load]);

  type Term = EntitiesResponse["terminals"][number];
  type Air = EntitiesResponse["aircraft"][number];
  type Tm = EntitiesResponse["located"][number];

  const ent = (idv: string) => `/entity/${encodeURIComponent(idv)}`;
  const termCols: Column<Term>[] = [
    { key: "id", header: "Identity", cell: (e) => {
      const linkId = e.imsi || e.imei || e.tmsis[0] || e.id;
      const label = e.imsi ? `IMSI ${e.imsi}` : e.imei ? `IMEI ${e.imei}` : e.tmsis[0] ? `TMSI ${e.tmsis[0]}` : e.id;
      return (
        <>
          <Link to={ent(linkId)}><code>{label}</code></Link>{" "}
          {e.via === "call" && <span className="hl-muted" title="Identified via call signalling (downlink), not an uplink identity">· call</span>}
        </>
      );
    } },
    { key: "tmsis", header: "TMSIs", cell: (e) => (e.tmsis.length ? e.tmsis.join(", ") : dash) },
    { key: "msisdns", header: "MSISDN", cell: (e) => (e.msisdns.length ? <span title={e.via === "call" ? "Call contact (on downlink, the calling party)" : undefined}>{e.msisdns.join(", ")}</span> : dash) },
    { key: "loc", header: "Location", cell: (e) => loc(e.location) },
    { key: "sensors", header: "Sensors", cell: (e) => e.sensors.length },
    { key: "last", header: "Last seen", cell: (e) => timeAgo(e.last_seen) },
  ];
  const airCols: Column<Air>[] = [
    { key: "tail", header: "Aircraft (tail)", cell: (a) => <Link to={ent(a.tail)}><code>{a.tail}</code></Link> },
    { key: "obs", header: "Msgs", cell: (a) => a.observations },
    { key: "recent", header: "Recent", cell: (a) => (a.recent ? <span title={a.recent}>{a.recent.replace(/^Iridium (ACARS|SBD)[^:]*:?\s*/, "").slice(0, 60) || a.recent.slice(0, 60)}</span> : dash) },
    { key: "sensors", header: "Sensors", cell: (a) => a.sensors },
    { key: "last", header: "Last seen", cell: (a) => timeAgo(a.last_seen) },
  ];
  const tmCols: Column<Tm>[] = [
    { key: "tmsi", header: "TMSI", cell: (t) => <Link to={ent(t.tmsi)}><code>{t.tmsi}</code></Link> },
    { key: "loc", header: "Location", cell: (t) => loc(t.location) },
    { key: "sensors", header: "Sensor", cell: (t) => t.sensors.join(", ") },
    { key: "last", header: "Last seen", cell: (t) => timeAgo(t.last_seen) },
  ];

  return (
    <div className="hl-page hl-stack">
      <IcPageHeader heading="Entities" subheading="Fused targets — identities, aircraft, and located TMSIs across the fleet">
        <IcButton slot="actions" variant="secondary" onClick={load}>Refresh</IcButton>
      </IcPageHeader>

      {error ? <ErrorState error={error} onRetry={load} /> : !data ? <Loading label="Fusing…" /> : (
        <>
          <section className="hl-panel hl-stack">
            <h3>Cellular terminals ({data.meta.terminals})</h3>
            {data.terminals.length ? (
              <>
                {data.meta.terminals_via_call ? (
                  <p className="hl-muted">
                    {data.meta.terminals_via_call} identified via <strong>call signalling</strong>{" "}
                    (TMSI tied to an MSISDN seen in a CC&nbsp;Setup). On a downlink‑only site that
                    MSISDN is the <em>calling party</em>; the geolocatable target is the TMSI.
                    IMSI/IMEI identities still require uplink.
                  </p>
                ) : null}
                <DataTable caption="Cellular terminals" rows={data.terminals} columns={termCols} rowKey={(e) => e.id} />
              </>
            ) : (
              <p className="hl-muted">
                None yet. Terminals appear two ways: an IMSI/IMEI identity (uplink‑only — needs a
                terminal transmitting in range), or a TMSI caught in call signalling with an
                associated MSISDN (works on the downlink). Neither has been seen yet.
              </p>
            )}
          </section>

          <section className="hl-panel hl-stack">
            <h3>Aircraft ({data.meta.aircraft})</h3>
            {data.aircraft.length ? (
              <DataTable caption="Aircraft" rows={data.aircraft} columns={airCols} rowKey={(a) => a.id} />
            ) : <p className="hl-muted">No ACARS aircraft seen yet.</p>}
          </section>

          <section className="hl-panel hl-stack">
            <h3>Located TMSIs — unidentified targets ({data.meta.located} shown of {data.meta.geolocated_tmsis} geolocated)</h3>
            {data.located.length ? (
              <DataTable caption="Located TMSIs" rows={data.located} columns={tmCols} rowKey={(t) => t.id} />
            ) : <p className="hl-muted">No geolocated TMSIs yet.</p>}
          </section>
        </>
      )}
    </div>
  );
}
